import { getCloudItem, setCloudItem } from '@/shared/utils/cloudStorage';
import { makeAutoObservable, runInAction } from 'mobx';

import SoundNoise1 from '@/shared/sounds/noise1.mp3';
import SoundNoise2 from '@/shared/sounds/noise2.mp3';
import SoundNoise3 from '@/shared/sounds/noise3.mp3';
import SoundNoise4 from '@/shared/sounds/noise4.mp3';
import SoundNoise5 from '@/shared/sounds/noise5.mp3';
import SoundNoise6 from '@/shared/sounds/noise6.mp3';
import SoundNoise7 from '@/shared/sounds/noise7.mp3';

import SoundGoal1 from '@/shared/sounds/goal1.mp3';
import SoundGoal2 from '@/shared/sounds/goal2.mp3';
import SoundGoal3 from '@/shared/sounds/goal3.mp3';
import SoundGoal4 from '@/shared/sounds/goal4.mp3';
import SoundGoal5 from '@/shared/sounds/goal5.mp3';
import SoundGoal6 from '@/shared/sounds/goal6.mp3';
import SoundGoal7 from '@/shared/sounds/goal7.mp3';

import SoundMiss1 from '@/shared/sounds/miss1.mp3';
import SoundMiss2 from '@/shared/sounds/miss2.mp3';
import SoundMiss3 from '@/shared/sounds/miss3.mp3';
import SoundMiss4 from '@/shared/sounds/miss4.mp3';
import SoundMiss5 from '@/shared/sounds/miss5.mp3';
import SoundMiss6 from '@/shared/sounds/miss6.mp3';
import SoundMiss7 from '@/shared/sounds/miss7.mp3';

import { GameState } from '@/entities/game/types';

export type SoundKey = 'noise' | 'goal' | 'miss';

const SOUND_PATHS: Record<SoundKey, string[]> = {
  noise: [SoundNoise1, SoundNoise2, SoundNoise3, SoundNoise4, SoundNoise5, SoundNoise6, SoundNoise7],
  goal: [SoundGoal1, SoundGoal2, SoundGoal3, SoundGoal4, SoundGoal5, SoundGoal6, SoundGoal7],
  miss: [SoundMiss1, SoundMiss2, SoundMiss3, SoundMiss4, SoundMiss5, SoundMiss6, SoundMiss7],
};

export class SoundStore {
  private sounds: Partial<Record<SoundKey, HTMLAudioElement[]>> = {};
  private noiseIndex = 0;

  enabled = false;
  ready = false;
  volume = 0.5;

  constructor() {
    makeAutoObservable(this);
    this.loadSounds();
    this.init();
  }

  private loadSounds() {
    for (const key of Object.keys(SOUND_PATHS) as SoundKey[]) {
      const paths = SOUND_PATHS[key];
      this.sounds[key] = paths.map((src) => {
        const audio = new Audio(src);
        audio.volume = this.volume;
        audio.loop = false;
        audio.onended = null;
        return audio;
      });
    }
  }

  private async init() {
    try {
      const [enabledRaw, volumeRaw] = await Promise.all([
        getCloudItem('soundEnabled', String(this.enabled)),
        getCloudItem('soundVolume', String(this.volume)),
      ]);


      runInAction(() => {
        this.enabled = enabledRaw === 'true';
        this.volume = volumeRaw ? parseFloat(volumeRaw) || 0.5 : 0.5;
        this.ready = true;
      });

      if (this.enabled) this.play('noise');
    } catch (e) {
      console.warn('Ошибка инициализации звука', e);
      runInAction(() => (this.ready = true));
    }
  }

  async setEnabled(value: boolean) {
    this.enabled = value;
    await setCloudItem('soundEnabled', String(value));
    if (value) this.play('noise');
    else this.stopAll();
  }

  async toggleSound(state?: GameState) {
    const next = !this.enabled;
    this.enabled = next;

    await setCloudItem('soundEnabled', String(next));
    if (next && state === GameState.active) this.play('noise');
    else this.stopAll();
  }

  async setVolume(value: number) {
    const clamped = Math.max(0, Math.min(1, value));
    this.volume = clamped;
    // обновляем все элементы
    (Object.values(this.sounds).flat() as HTMLAudioElement[]).forEach((sound) => {
      sound.volume = clamped;
    });
    await setCloudItem('soundVolume', String(clamped));
  }

  play(name: SoundKey) {
    if (!this.enabled) return;

    const list = this.sounds[name];
    if (!list || list.length === 0) return;

    if (name === 'noise') {
      this.playNextNoise();
    } else {
      this.playRandomFromCategory(name);
    }
  }

  private playNextNoise() {
    const list = this.sounds.noise;
    if (!list || !list.length) return;

    list.forEach((s, i) => {
      if (i !== this.noiseIndex) {
        s.pause();
        s.currentTime = 0;
        s.onended = null;
      }
    });

    const sound = list[this.noiseIndex];
    sound.currentTime = 0;
    sound.volume = this.volume;

    sound.onended = () => {
      if (!this.enabled) return;

      sound.onended = null;
      this.noiseIndex = (this.noiseIndex + 1) % list.length;
      if (this.enabled) {
        this.playNextNoise();
      }
    };

    sound.play().catch(() => {});
  }

  private playRandomFromCategory(name: 'goal' | 'miss') {
    const list = this.sounds[name];
    if (!list || !list.length) return;

    list.forEach((s) => {
      s.pause();
      s.currentTime = 0;
      s.onended = null;
    });

    const idx = Math.floor(Math.random() * list.length);
    const sound = list[idx];
    sound.currentTime = 0;
    sound.volume = this.volume;

    sound.onended = null;
    sound.play().catch(() => {});
  }

  stop(name: SoundKey) {
    const list = this.sounds[name];
    if (!list) return;
    list.forEach((sound) => {
      sound.onended = null;
      sound.pause();
      sound.currentTime = 0;
    });
  }

  stopAll() {
    (Object.values(this.sounds).flat() as HTMLAudioElement[]).forEach((sound) => {
      sound.onended = null;
      sound.pause();
      sound.currentTime = 0;
    });
  }
}
